
<template>
  <div>
    <div class="flex justify-between flex-col sm:flex-row" id="education-information">
      <div>
        <h2 class="font-medium text-base md:text-xl">Educational Background</h2>
        <span class="text-gray-600 text-sm md:text-base font-medium">View & update your educational information wherever possible</span>
      </div>
      <div class="w-full sm:w-auto mt-4 sm:mt-0">
        <button class="w-full flex shadow justify-center items-center px-4 py-3 border rounded-lg border-blue-400 max:lg:p-2 max:lg:mt-0" @click="showAddEducationModal = !showAddEducationModal">
          <svg class="h-6 w-6 text-blue-700 fill-current max:lg:h-4 max:lg:w-4" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11 5H9V9H5V11H9V15H11V11H15V9H11V5Z" />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M0 10C0 4.486 4.486 0 10 0C15.514 0 20 4.486 20 10C20 15.514 15.514 20 10 20C4.486 20 0 15.514 0 10ZM2 10C2 14.411 5.589 18 10 18C14.411 18 18 14.411 18 10C18 5.589 14.411 2 10 2C5.589 2 2 5.589 2 10Z"
            />
          </svg>

          <span class="ml-2 font-medium text-blue-700 ">Add New Education</span>
        </button>
      </div>
    </div>
    <div class="mt-8" v-if="educations.length">
      <div
        tabindex="0"
        :key="index"
        v-for="(education, index) in educations"
        class="border rounded bg-white p-4 mt-4 flex items-center justify-between focus:outline-none focus:border focus:border-blue-500 hover:shadow"
      >
        <div class="flex flex-col md:flex-row md:justify-between md:items-center w-full gap-4">
          <div>
          <p class="font-medium text-gray-700">
            <span class="uppercase">{{ education.degree_title }}</span>
            <span class="text-blue-500 font-medium" v-if="education.academic_score">
              ({{ education.academic_score }}{{ $options.scoreTypeSymbolMap[education.academic_score_type] }})
            </span>
          </p>
          <p>{{ education.institution }}</p>
          <p v-if="education.isChanged" class="text-slate-600 font-normal leading-normal" style="font-size: 10px">
            <icon-base iconName="Info" width="12" height="12" class="inline" /> The changes you have made are yet to be reviewed.
          </p>
        </div>
        <div class="text-gray-700 font-medium flex items-end">
          <span v-if="education.course_start && education.course_end" class="inline-block border text-sm px-2 py-1 bg-gray-200 rounded">
            {{ getFormattedDate(education.course_start) }} -
            {{ getFormattedDate(education.course_end) }}
          </span>
        </div>
        </div>
        <button @click="() => toggleEditPopup(education)" class="ml-6 focus:outline-none cursor-pointer"><icon-base iconName="Edit"></icon-base></button>
      </div>
    </div>
    <NoRecords v-else>
      <div class="flex items-center flex-col">It seems you haven't added your any education info yet.</div>
    </NoRecords>
    <div class="mt-10" id="english-test-score">
      <div class="flex justify-between">
        <div>
          <h2 class="font-medium text-base md:text-xl">English Test Score</h2>
          <span class="text-gray-600 text-sm md:text-base font-medium"> View & update your english Test Score wherever possible </span>
        </div>
        <button @click="toggleEditable" class="focus:outline-none cursor-pointer mx-4"><icon-base iconName="Edit"></icon-base></button>
      </div>
      <form @submit.prevent="submit">
        <div class="mt-6">
          <p class="font-medium">TOEFL</p>
          <p v-if="test_scores.toefl.isChanged" class="text-slate-600 font-normal leading-normal mt-2" style="font-size: 10px">
            <icon-base iconName="Info" width="12" height="12" class="mr-2" /> The changes you have made are yet to be reviewed.
          </p>
          <div class="mt-3 lg:inline-flex lg:flex-no-wrap w-full grid grid-cols-4 gap-y-4">
            <AgInput
              v-validate="{ required: formValidations.toefl.isRequired }"
              class="lg:w-1/5"
              type="number"
              :disabled="!editable"
              label="Listening"
              name="toefl.band_score.listening.score"
              v-model="test_scores.toefl.band_score.listening.score"
              :min="$options.CLIENT_PROFILE_CONFIG.TOEFL.min"
              :max="$options.CLIENT_PROFILE_CONFIG.TOEFL.max"
              :step="$options.CLIENT_PROFILE_CONFIG.TOEFL.step"
            >
            </AgInput>
            <AgInput
              v-validate="{ required: formValidations.toefl.isRequired }"
              class="ml-2 lg:w-1/5"
              type="number"
              :disabled="!editable"
              label="Reading"
              name="toefl.band_score.reading.score"
              v-model="test_scores.toefl.band_score.reading.score"
              :min="$options.CLIENT_PROFILE_CONFIG.TOEFL.min"
              :max="$options.CLIENT_PROFILE_CONFIG.TOEFL.max"
              :step="$options.CLIENT_PROFILE_CONFIG.TOEFL.step"
            >
            </AgInput>
            <AgInput
              v-validate="{ required: formValidations.toefl.isRequired }"
              class="ml-2 lg:w-1/5"
              type="number"
              :disabled="!editable"
              label="Writing"
              name="toefl.band_score.writing.score"
              v-model="test_scores.toefl.band_score.writing.score"
              :min="$options.CLIENT_PROFILE_CONFIG.TOEFL.min"
              :max="$options.CLIENT_PROFILE_CONFIG.TOEFL.max"
              :step="$options.CLIENT_PROFILE_CONFIG.TOEFL.step"
            >
            </AgInput>
            <AgInput
              v-validate="{ required: formValidations.toefl.isRequired }"
              class="ml-2 lg:w-1/5"
              type="number"
              :disabled="!editable"
              label="Speaking"
              name="toefl.band_score.speaking.score"
              v-model="test_scores.toefl.band_score.speaking.score"
              :min="$options.CLIENT_PROFILE_CONFIG.TOEFL.min"
              :max="$options.CLIENT_PROFILE_CONFIG.TOEFL.max"
              :step="$options.CLIENT_PROFILE_CONFIG.TOEFL.step"
            >
            </AgInput>
            <AgInput
              v-validate="{ required: formValidations.toefl.isRequired }"
              class="ml-0 lg:w-1/5 lg:ml-2"
              type="number"
              :disabled="!editable"
              label="Overall Score"
              name="toefl.overall_score"
              v-model="test_scores.toefl.overall_score"
              :min="$options.CLIENT_PROFILE_CONFIG.TOEFL.min"
              :max="$options.CLIENT_PROFILE_CONFIG.TOEFL.overall"
              :step="$options.CLIENT_PROFILE_CONFIG.TOEFL.step"
            >
            </AgInput>
          </div>
        </div>
        <div class="mt-6">
          <p class="font-medium">IELTS</p>
          <p v-if="test_scores.ielts.isChanged" class="text-slate-600 font-normal leading-normal mt-2" style="font-size: 10px">
            <icon-base iconName="Info" width="12" height="12" class="mr-2" /> The changes you have made are yet to be reviewed.
          </p>
          <div class="mt-3 lg:inline-flex lg:flex-no-wrap w-full grid grid-cols-4 gap-y-4">
            <AgInput
              v-validate="{ required: formValidations.ielts.isRequired }"
              class="lg:w-1/5"
              type="number"
              :disabled="!editable"
              label="Listening"
              name="ielts.band_score.listening.score"
              v-model="test_scores.ielts.band_score.listening.score"
              :min="$options.CLIENT_PROFILE_CONFIG.IELTS.min"
              :max="$options.CLIENT_PROFILE_CONFIG.IELTS.max"
              :step="$options.CLIENT_PROFILE_CONFIG.IELTS.step"
            >
            </AgInput>
            <AgInput
              v-validate="{ required: formValidations.ielts.isRequired }"
              class="ml-2 lg:w-1/5"
              type="number"
              :disabled="!editable"
              label="Reading"
              name="ielts.band_score.reading.score"
              v-model="test_scores.ielts.band_score.reading.score"
              :min="$options.CLIENT_PROFILE_CONFIG.IELTS.min"
              :max="$options.CLIENT_PROFILE_CONFIG.IELTS.max"
              :step="$options.CLIENT_PROFILE_CONFIG.IELTS.step"
            >
            </AgInput>
            <AgInput
              v-validate="{ required: formValidations.ielts.isRequired }"
              class="ml-2 lg:w-1/5"
              type="number"
              :disabled="!editable"
              label="Writing"
              name="ielts.band_score.writing.score"
              v-model="test_scores.ielts.band_score.writing.score"
              :min="$options.CLIENT_PROFILE_CONFIG.IELTS.min"
              :max="$options.CLIENT_PROFILE_CONFIG.IELTS.max"
              :step="$options.CLIENT_PROFILE_CONFIG.IELTS.step"
            >
            </AgInput>
            <AgInput
              v-validate="{ required: formValidations.ielts.isRequired }"
              class="ml-2 lg:w-1/5"
              type="number"
              :disabled="!editable"
              label="Speaking"
              name="ielts.band_score.speaking.score"
              v-model="test_scores.ielts.band_score.speaking.score"
              :min="$options.CLIENT_PROFILE_CONFIG.IELTS.min"
              :max="$options.CLIENT_PROFILE_CONFIG.IELTS.max"
              :step="$options.CLIENT_PROFILE_CONFIG.IELTS.step"
            >
            </AgInput>
            <AgInput
              v-validate="{ required: formValidations.ielts.isRequired }"
              class="ml-0 lg:w-1/5 lg:ml-2"
              type="number"
              :disabled="!editable"
              label="Overall Score"
              name="ielts.overall_score"
              v-model="test_scores.ielts.overall_score"
              :min="$options.CLIENT_PROFILE_CONFIG.IELTS.min"
              :max="$options.CLIENT_PROFILE_CONFIG.IELTS.overall"
              :step="$options.CLIENT_PROFILE_CONFIG.IELTS.step"
            >
            </AgInput>
          </div>
        </div>
        <div class="mt-6">
          <p class="font-medium">PTE</p>
          <p v-if="test_scores.pte.isChanged" class="text-slate-600 font-normal leading-normal mt-2" style="font-size: 10px">
            <icon-base iconName="Info" width="12" height="12" class="mr-2" /> The changes you have made are yet to be reviewed.
          </p>
          <div class="mt-3 lg:inline-flex lg:flex-no-wrap w-full grid grid-cols-4 gap-y-4">
            <AgInput
              v-validate="{ required: formValidations.pte.isRequired }"
              class="lg:w-1/5"
              type="number"
              :disabled="!editable"
              label="Listening"
              name="pte.band_score.listening.score"
              v-model="test_scores.pte.band_score.listening.score"
              :min="$options.CLIENT_PROFILE_CONFIG.PTE.min"
              :max="$options.CLIENT_PROFILE_CONFIG.PTE.max"
              :step="$options.CLIENT_PROFILE_CONFIG.PTE.step"
            >
            </AgInput>
            <AgInput
              v-validate="{ required: formValidations.pte.isRequired }"
              class="ml-2 lg:w-1/5"
              type="number"
              :disabled="!editable"
              label="Reading"
              name="pte.band_score.reading.score"
              v-model="test_scores.pte.band_score.reading.score"
              :min="$options.CLIENT_PROFILE_CONFIG.PTE.min"
              :max="$options.CLIENT_PROFILE_CONFIG.PTE.max"
              :step="$options.CLIENT_PROFILE_CONFIG.PTE.step"
            >
            </AgInput>
            <AgInput
              v-validate="{ required: formValidations.pte.isRequired }"
              class="ml-2 lg:w-1/5"
              type="number"
              :disabled="!editable"
              label="Writing"
              name="pte.band_score.writing.score"
              v-model="test_scores.pte.band_score.writing.score"
              :min="$options.CLIENT_PROFILE_CONFIG.PTE.min"
              :max="$options.CLIENT_PROFILE_CONFIG.PTE.max"
              :step="$options.CLIENT_PROFILE_CONFIG.PTE.step"
            >
            </AgInput>
            <AgInput
              v-validate="{ required: formValidations.pte.isRequired }"
              class="ml-2 lg:w-1/5"
              type="number"
              :disabled="!editable"
              label="Speaking"
              name="pte.band_score.speaking.score"
              v-model="test_scores.pte.band_score.speaking.score"
              :min="$options.CLIENT_PROFILE_CONFIG.PTE.min"
              :max="$options.CLIENT_PROFILE_CONFIG.PTE.max"
              :step="$options.CLIENT_PROFILE_CONFIG.PTE.step"
            >
              <span class="text-sm text-red-700">
                {{ errors.first('academic_score') }}
              </span>
            </AgInput>
            <AgInput
              v-validate="formValidations.pte.isRequired && 'required'"
              class="ml-0 lg:w-1/5 lg:ml-2"
              type="number"
              :disabled="!editable"
              label="Overall Score"
              name="pte.overall_score"
              v-model="test_scores.pte.overall_score"
              :min="$options.CLIENT_PROFILE_CONFIG.PTE.min"
              :max="$options.CLIENT_PROFILE_CONFIG.PTE.overall"
              :step="$options.CLIENT_PROFILE_CONFIG.PTE.step"
            >
              <span class="text-sm text-red-700">
                {{ errors.first('academic_score') }}
              </span>
            </AgInput>
          </div>
        </div>
        <div class="mt-10 mb-24 flex flex-col-reverse md:flex-row gap-4 justify-end" v-if="editable">
          <AgButton class="w-full md:w-auto" buttonType="primary" buttonColor="gray" type="button" @onClick="cancelChanges"> Cancel </AgButton>
          <AgButton class="w-full md:w-auto m-0" buttonType="primary" type="submit"> Save changes </AgButton>
        </div>
      </form>
    </div>
    <AddEducationModal v-if="showAddEducationModal" :selectedData="selectedData" @close="closeAddEducationModal" @success="fetch"></AddEducationModal>
  </div>
</template>

<script>
import moment from 'moment';
import AgInput from '@/components/Input/Input.vue';
import NoRecords from '@/components/NoRecords.vue';
import AgButton from '@/components/Button.vue';
import AddEducationModal from './AddEducation';
import { CLIENT_PROFILE_CONFIG } from '../../config/app';
import { getEducationDetails, updateTestScores } from '../../api/calls';
import cloneDeep from 'lodash.clonedeep';

const scoreTypeSymbolMap = {
  GPA: 'GPA',
  percentage: '%'
};

const initialScoresForm = {
  pte: {
    test_id: 3,
    test_name: 'PTE',
    isChanged: false,
    overall_score: 0,
    band_score: {
      listening: {
        score: 0
      },
      speaking: {
        score: 0
      },
      writing: {
        score: 0
      },
      reading: {
        score: 0
      }
    }
  },
  toefl: {
    test_id: 1,
    test_name: 'TOEFL',
    isChanged: false,
    overall_score: 0,
    band_score: {
      listening: {
        score: 0
      },
      speaking: {
        score: 0
      },
      writing: {
        score: 0
      },
      reading: {
        score: 0
      }
    }
  },
  ielts: {
    test_id: 2,
    test_name: 'IELTS',
    isChanged: false,
    overall_score: 0,
    band_score: {
      listening: {
        score: 0
      },
      speaking: {
        score: 0
      },
      writing: {
        score: 0
      },
      reading: {
        score: 0
      }
    }
  }
};

export default {
  name: 'EducationInformation',
  scoreTypeSymbolMap,
  CLIENT_PROFILE_CONFIG,
  components: {
    AgInput,
    NoRecords,
    AddEducationModal,
    AgButton,
    IconBase: () => import('@/components/icons/index.vue')
  },
  data() {
    return {
      showAddEducationModal: false,
      educations: [],
      test_scores_default: { ...initialScoresForm },
      formValidations: {
        toefl: {
          isRequired: false
        },
        pte: {
          isRequired: false
        },
        ielts: {
          isRequired: false
        }
      },
      test_scores: {
        ...initialScoresForm
      },
      editable: false,
      selectedData: null,
      isScoresReset: false,
      modifiedFields: {
        educations: [],
        test_scores: {}
      }
    };
  },
  computed: {
    isFormDirty() {
      return Object.keys(this.fields).some(key => this.fields[key].dirty);
    },
    modifiedFieldsList() {
      return this.fields;
    }
  },
  async beforeRouteEnter(to, from, next) {
    try {
      const response = await getEducationDetails();
      next(vm => {
        vm.educations = vm.formatEducationList(response.data.educations);
        vm.formatTestScores(response.data.englishTestScore);
      });
    } catch (error) {
      throw error;
    }
  },
  methods: {
    getFormattedDate(inputDate) {
      return moment(new Date(inputDate)).format('MMM YYYY');
    },
    formatEducationList(educationList = []) {
      return educationList.map(eachEducation => {
        const { changes, course_start, course_end, need_approval, ...rest } = eachEducation;
        return {
          course_start:
            course_start && course_start.actual !== '' ? (course_start.actual ? moment(course_start.actual).format('yyyy/MM') : moment(course_start).format('yyyy/MM')) : undefined,
          course_end: course_end && course_end.actual !== '' ? (course_end.actual ? moment(course_end.actual).format('yyyy/MM') : moment(course_end).format('yyyy/MM')) : undefined,
          ...rest,
          ...changes,
          isChanged: changes ? true : need_approval ? need_approval : false,
          changes: { ...changes }, //for checking modified fields
          need_approval: need_approval || false,
          default: { ...rest, course_start, course_end }
        };
      });
    },
    formatTestScores(scoresList) {
      const toeflChanges = scoresList.TOEFL && scoresList.TOEFL.changes;
      const ieltsChanges = scoresList.IELTS && scoresList.IELTS.changes;
      const pteChanges = scoresList.PTE && scoresList.PTE.changes;
      this.modifiedFields.test_scores = { ielts: ieltsChanges, toefl: toeflChanges, pte: pteChanges };
      const testScores = {
        pte: {
          ...initialScoresForm.pte,
          isChanged: !!pteChanges,
          ...(scoresList &&
            scoresList.PTE && {
              ...scoresList.PTE,
              band_score: {
                listening: { ...scoresList.PTE.band_score.Listening },
                reading: { ...scoresList.PTE.band_score.Reading },
                speaking: { ...scoresList.PTE.band_score.Speaking },
                writing: { ...scoresList.PTE.band_score.Writing }
              }
            })
        },
        toefl: {
          ...initialScoresForm.toefl,
          isChanged: !!toeflChanges,
          ...(scoresList &&
            scoresList.TOEFL && {
              ...scoresList.TOEFL,
              band_score: {
                listening: { ...scoresList.TOEFL.band_score.Listening },
                reading: { ...scoresList.TOEFL.band_score.Reading },
                speaking: { ...scoresList.TOEFL.band_score.Speaking },
                writing: { ...scoresList.TOEFL.band_score.Writing }
              }
            })
        },
        ielts: {
          ...initialScoresForm.ielts,
          isChanged: !!ieltsChanges,
          ...(scoresList &&
            scoresList.IELTS && {
              ...scoresList.IELTS,
              band_score: {
                listening: { ...scoresList.IELTS.band_score.Listening },
                reading: { ...scoresList.IELTS.band_score.Reading },
                speaking: { ...scoresList.IELTS.band_score.Speaking },
                writing: { ...scoresList.IELTS.band_score.Writing }
              }
            })
        }
      };
      // this.test_scores_default = cloneDeep(testScores);
      testScores.pte = { ...testScores.pte, ...pteChanges };
      testScores.toefl = { ...testScores.toefl, ...toeflChanges };
      testScores.ielts = { ...testScores.ielts, ...ieltsChanges };
      this.test_scores = testScores;
    },
    async fetch() {
      try {
        const response = await getEducationDetails();
        //on editing score, pass the value to test_scores and if changed field is present, set changed to true and pass changed value there.
        this.$nextTick(() => {
          this.educations = this.formatEducationList(response.data.educations);
          this.formatTestScores(response.data.englishTestScore);
        });
      } catch (error) {
        throw error;
      }
    },
    toggleEditable() {
      this.editable = !this.editable;
    },
    async submit() {
      try {
        this.$loading(true);
        await updateTestScores(this.responseFormatter(this.test_scores));
        this.$toasted.success('Your english test scores have been updated sucessfully');
      } catch (error) {
        this.$toasted.error('Oops. An Error Occured.');
      } finally {
        this.isScoresReset = false;
        this.$loading(false);
      }
    },
    toggleEditPopup(selected) {
      this.selectedData = selected;
      this.showAddEducationModal = !this.showAddEducationModal;
    },
    closeAddEducationModal() {
      this.selectedData = null;
      this.showAddEducationModal = false;
    },
    responseFormatter(inputField) {
      const {
        pte: { isChanged: pteDataChanged, ...pteRest },
        ielts: { isChanged: ieltsDataChanged, ...ieltsRest },
        toefl: { isChanged: toeflDataChanged, ...toeflRest }
      } = inputField;

      return {
        test_scores: {
          ...((this.shouldUpdate('toefl') || toeflDataChanged || this.isScoresReset) && {
            toefl: {
              ...toeflRest
            }
          }),
          ...((this.shouldUpdate('ielts') || ieltsDataChanged || this.isScoresReset) && {
            ielts: {
              ...ieltsRest
            }
          }),
          ...((this.shouldUpdate('pte') || pteDataChanged || this.isScoresReset) && {
            pte: {
              ...pteRest
            }
          })
        }
      };
    },
    cancelChanges() {
      this.test_scores = cloneDeep({
        ...this.test_scores_default,
        toefl: { ...this.test_scores_default.toefl },
        ielts: { ...this.test_scores_default.ielts },
        pte: { ...this.test_scores_default.pte }
      });
      this.isScoresReset = true;
    },
    shouldUpdate(key) {
      return this.modifiedFieldsList[`${key}.band_score.listening.score`].dirty ||
        this.modifiedFieldsList[`${key}.band_score.speaking.score`].dirty ||
        this.modifiedFieldsList[`${key}.band_score.reading.score`].dirty ||
        this.modifiedFieldsList[`${key}.band_score.writing.score`].dirty ||
        this.modifiedFieldsList[`${key}.overall_score`].dirty
        ? true
        : false;
    }
  }
};
</script>
<style lang="scss" scoped>
.button-container {
  button {
    &:last-child {
      margin-left: 10px;
    }
  }
}
</style>
